/**
 * A tiny declarative parser for command payloads.
 *
 * Requests arrive as untyped JSON (console, replay, tour data, URL), so every
 * definition's `validate` checks shape first. A field spec maps each key to a
 * primitive kind; a trailing `?` makes it optional. Unknown keys are dropped,
 * so the journal only ever records canonical payloads.
 *
 * Domain validation (does the entity exist, is the value in range) happens in
 * the owning module after shape parsing.
 */
import type { ValidationResult } from "./types";

type FieldKind = "string" | "number" | "boolean" | "string[]" | "unknown";
type FieldSpec = FieldKind | `${FieldKind}?`;

interface KindTypes {
  string: string;
  number: number;
  boolean: boolean;
  "string[]": readonly string[];
  unknown: unknown;
}

type Required<S> = { [K in keyof S as S[K] extends `${string}?` ? never : K]: S[K] };
type Optional<S> = { [K in keyof S as S[K] extends `${string}?` ? K : never]: S[K] };

/** The typed payload a spec describes. */
export type PayloadOf<S extends Record<string, FieldSpec>> = {
  readonly [K in keyof Required<S>]: S[K] extends FieldKind ? KindTypes[S[K]] : never;
} & {
  readonly [K in keyof Optional<S>]?: S[K] extends `${infer B extends FieldKind}?`
    ? KindTypes[B]
    : never;
};

const CHECKS: Record<FieldKind, { test: (value: unknown) => boolean; expected: string }> = {
  string: { test: (v) => typeof v === "string", expected: "a string" },
  number: { test: (v) => typeof v === "number" && Number.isFinite(v), expected: "a number" },
  boolean: { test: (v) => typeof v === "boolean", expected: "a boolean" },
  "string[]": {
    test: (v) => Array.isArray(v) && v.every((item) => typeof item === "string"),
    expected: "an array of strings",
  },
  unknown: { test: () => true, expected: "any value" },
};

export function parsePayload<const S extends Record<string, FieldSpec>>(
  payload: unknown,
  spec: S,
): ValidationResult<PayloadOf<S>> {
  if (typeof payload !== "object" || payload === null || Array.isArray(payload)) {
    return { ok: false, error: "payload must be an object" };
  }

  const source = payload as Record<string, unknown>;
  const out: Record<string, unknown> = {};

  for (const [key, fieldSpec] of Object.entries(spec)) {
    const optional = fieldSpec.endsWith("?");
    const kind = (optional ? fieldSpec.slice(0, -1) : fieldSpec) as FieldKind;
    const value = source[key];

    if (value === undefined) {
      if (optional || kind === "unknown") continue;
      return { ok: false, error: `'${key}' is required` };
    }
    if (!CHECKS[kind].test(value)) {
      return { ok: false, error: `'${key}' must be ${CHECKS[kind].expected}` };
    }
    out[key] = value;
  }

  return { ok: true, value: out as PayloadOf<S> };
}
